import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import api from '../services/api';

interface User {
  id: number;
  email: string;
  role: string;
  two_factor_enabled: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, checkboxTime?: number) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));

  // Load user data on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && !user) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
          setToken(storedToken);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
    };
    loadUser();
  }, [user]);

  const login = async (email: string, password: string, checkboxTime?: number) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
        checkboxTime // Send timestamp for bot detection
      });
      const { token: authToken, user: userData } = response.data;

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('token', authToken);

      // Update satellite image cache on every login
      console.log('📡 Updating satellite image cache...');
      try {
        await api.post('/satellite/update-cache');
        console.log('✅ Satellite cache updated');
      } catch (error) {
        console.error('⚠️  Failed to update satellite cache:', error);
        // Non-critical error, don't block login
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.error || 'Login failed. Please check your credentials.');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      logout,
      isAuthenticated: !!token,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
